import { createContext, useContext, useEffect, useRef } from "react";
import type { AnalyticsEventName, RawData } from "./analytics";
import type { AnalyticsClient } from "./analytics-client";

type AnalyticsContextValue = {
    track: (eventName: AnalyticsEventName, rawData?: RawData) => Promise<void>;
    trackBatch: (
        events: Array<{ event_name: AnalyticsEventName; raw_data?: RawData }>,
    ) => Promise<void>;
};

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

type AnalyticsProviderProps = {
    client: AnalyticsClient;
    children: React.ReactNode;
};

/**
 * Provider component that makes analytics available to child components
 */
export function AnalyticsProvider({ client, children }: AnalyticsProviderProps) {
    const clientRef = useRef(client);

    useEffect(() => {
        clientRef.current = client;
    }, [client]);

    const track = async (eventName: AnalyticsEventName, rawData?: RawData) => {
        await clientRef.current.trackEvent({
            event_name: eventName,
            raw_data: rawData,
        });
    };

    const trackBatch = async (
        events: Array<{ event_name: AnalyticsEventName; raw_data?: RawData }>,
    ) => {
        await clientRef.current.trackEvents(events);
    };

    return (
        <AnalyticsContext.Provider value={{ track, trackBatch }}>
            {children}
        </AnalyticsContext.Provider>
    );
}

/**
 * Hook to access analytics tracking functions
 *
 * @example
 * ```tsx
 * const analytics = useAnalytics();
 *
 * // Track a simple event
 * analytics.track(AnalyticsEventName.VIEW_DISCOVER_OPENED);
 *
 * // Track with custom data
 * analytics.track(AnalyticsEventName.ARTICLE_OPENED, {
 *   article_id: "123",
 *   article_title: "My Article"
 * });
 * ```
 */
export function useAnalytics(): AnalyticsContextValue {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error("useAnalytics must be used within an AnalyticsProvider");
    }
    return context;
}
